import { ErrorHandler, inject, Injectable } from '@angular/core';
import { shareUntilDestroyed } from '@shared/utils/rxjs/rxjs-angular-interop/share-until-destroyed';
import { ConfirmService } from '@shared/shared/confirm/common/confirm.service';
import { DescriptionType } from '@shared/shared/confirm/common/interfaces/confirm-single-button-params';
import { LocationService } from '@shared/shared/navigation/location.service';
import { AtomType } from '@shared/modules/color-scheme/common/atom-type';
import { RegularStorage, RegularStorageItemKey } from '@shared/modules/storage/common/regular-storage';
import { DebugError } from '@shared/modules/storage/common/debug-error';
import {
  CreateRegularStorageConnectionOptions,
  RegularStorageService
} from '@shared/modules/storage/data-access/regular-storage.service';
import { deleteDB, IDBPDatabase, IDBPTransaction, openDB } from 'idb';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { concat, EMPTY, from, lastValueFrom, map, Observable, of, take } from 'rxjs';

/**
 * IndexedDB-backed {@link RegularStorageService}. Works in both the web and Electron renderer.
 *
 * On an unrecoverable error (corrupted store, blocked upgrade) it surfaces a confirm dialog and, if
 * needed, deletes and reloads to recover rather than leaving the app in a broken state.
 */
@Injectable()
export class BrowserRegularStorageService extends RegularStorageService {
  private readonly errorHandler: ErrorHandler = inject(ErrorHandler);
  private readonly confirmService: ConfirmService = inject(ConfirmService);
  private readonly locationService: LocationService = inject(LocationService);

  createStorageConnection<K extends RegularStorageItemKey, T>({
    storageName,
    key,
    destroyRef,
    version,
    asyncMigrationFns$
  }: CreateRegularStorageConnectionOptions): Observable<RegularStorage<K, T>> {
    const errorHandler = this.errorHandler;
    let pendingUpgradeError: unknown;
    const handleIdbErrorFn = (error: unknown): Observable<never> =>
      this.handleIdbError(pendingUpgradeError ?? error, storageName, undefined);

    return from(
      openDB(storageName, version ?? 1, {
        blocked: () => this.showBlockedDialog(),
        async upgrade(
          database: IDBPDatabase,
          oldVersion: number,
          newVersion: number | null,
          transaction: IDBPTransaction<unknown, string[], 'versionchange'>
        ) {
          try {
            if (!database.objectStoreNames.contains(storageName)) {
              database.createObjectStore(storageName, {
                autoIncrement: false,
                keyPath: key as string | string[]
              });
            }

            if (asyncMigrationFns$?.length && oldVersion > 0) {
              await lastValueFrom(
                concat(
                  ...asyncMigrationFns$.map(fn => fn({ database, oldVersion, newVersion, transaction, storageName }))
                )
              );
            }
          } catch (upgradeError) {
            // idb does not await the upgrade callback, so a thrown error would be silently lost and
            // openDB would hang indefinitely, triggering the database_blocked timeout instead.
            // Capture the original error and abort the transaction so openDB rejects immediately.
            pendingUpgradeError = upgradeError;
            transaction.abort();
          }
        }
      })
    ).pipe(
      catchError(handleIdbErrorFn),
      map(database => {
        const handleOperationErrorFn = (error: unknown): Observable<never> =>
          this.handleIdbError(error, storageName, database);

        return {
          read(accessKey: K): Observable<T | null> {
            return from(database.get(database.name, accessKey)).pipe(
              map(data => data ?? null),
              catchError(handleOperationErrorFn)
            );
          },
          readAll(): Observable<T[]> {
            return from(database.getAll(database.name)).pipe(catchError(handleOperationErrorFn));
          },
          store(accessKey: K, value: T): Observable<void> {
            const missingField = findMissingKeyPathField(key, value);
            if (missingField !== null) {
              errorHandler.handleError(
                new DebugError(
                  `IDB put skipped for "${storageName}": keyPath field "${missingField}" is missing on value`,
                  { storageName, keyPath: key, value }
                )
              );
              return of(undefined);
            }
            return from(database.put(database.name, value)).pipe(
              map(() => undefined),
              catchError(handleOperationErrorFn)
            );
          },
          delete(accessKey: K): Observable<void> {
            return from(database.delete(database.name, accessKey)).pipe(catchError(handleOperationErrorFn));
          },
          deleteAll(): Observable<void> {
            return from(database.clear(database.name)).pipe(catchError(handleOperationErrorFn));
          }
        };
      }),
      shareUntilDestroyed({ destroyRef })
    );
  }

  private showBlockedDialog(): void {
    this.confirmService
      .confirm({
        message: 'Storage update blocked',
        description:
          'Another open tab is preventing the app from updating its local storage. Close other tabs or reload to continue.',
        confirmMessage: 'Reload'
      })
      .pipe(
        take(1),
        tap(() => this.locationService.reloadPage())
      )
      .subscribe();
  }

  private handleIdbError(error: unknown, storageName: string, database: IDBPDatabase | undefined): Observable<never> {
    this.errorHandler.handleError(error);
    return this.confirmService
      .confirmWithSingleButton({
        message: 'Storage error',
        description: 'Some locally stored data could not be read and will be reset. The app will reload to recover.',
        confirmMessage: 'Reload',
        descriptionOptions: {
          atomType: AtomType.danger,
          descriptionType: DescriptionType.alert
        }
      })
      .pipe(
        tap(() => database?.close()),
        switchMap(() =>
          from(
            deleteDB(storageName, {
              blocked: () => this.showBlockedDialog()
            })
          )
        ),
        tap(() => this.locationService.reloadPage()),
        switchMap(() => EMPTY)
      );
  }
}

/**
 * Returns the first keyPath field that is missing on `value`, or `null` when every field is present.
 * IndexedDB rejects a put whose value lacks a keyPath field, so we guard against it up front.
 */
function findMissingKeyPathField(keyPath: string | readonly string[], value: unknown): string | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const paths: readonly string[] = typeof keyPath === 'string' ? [keyPath] : keyPath;
  for (const path of paths) {
    const fieldValue = (value as Record<string, unknown>)[path];
    if (fieldValue === undefined || fieldValue === null) {
      return path;
    }
  }
  return null;
}
